import React, { useEffect, useRef, useState } from "react";
import styles from "./imagePlayer.module.css";
import { Kampos, effects, transitions, noise } from "kampos";

const turbulence = effects.turbulence({
  noise: noise.perlinNoise,
});

const WIDTH = 640;
const HEIGHT = 452;
const CELL_FACTOR = 4;
const AMPLITUDE = CELL_FACTOR / WIDTH;

turbulence.frequency = { x: AMPLITUDE, y: AMPLITUDE };
turbulence.octaves = 8;
turbulence.isFractal = true;

export default function ImagePlayer({ comicCanvas, placeCanvas, progress }) {
  const [dissolve, setDissolve] = useState(null);
  const [hippo, setHippo] = useState(null);
  // const [imgProgress, setImgProgress] = useState(0);
  const canvasRef = useRef(null);

  const frames = [0, "place", 1, "place", 2, "place", 3];
  const totalImages = frames.length;

  useEffect(() => {
    if (!canvasRef.current || !comicCanvas) return;

    const mapTarget = document.createElement("canvas");
    mapTarget.width = WIDTH;
    mapTarget.height = HEIGHT;

    mapTarget.width = WIDTH;
    mapTarget.height = HEIGHT;

    const dissolveMap = new Kampos({
      target: mapTarget,
      effects: [turbulence],
      noSource: true,
    });
    dissolveMap.draw();

    const newDisolve = transitions.dissolve();
    newDisolve.map = mapTarget;
    newDisolve.high = 0.05; // low for crisp edges, high for smoke like blur
    setDissolve(newDisolve);

    const target = canvasRef.current;
    const newHippo = new Kampos({ target, effects: [newDisolve] });

    setHippo(newHippo);
    setDissolve(newDisolve);
  }, [canvasRef, comicCanvas]);

  useEffect(() => {
    if (!hippo || !dissolve || !placeCanvas) return;

    const { currIndex, nextIndex, transitionProgress, imgProgress } =
      getCurrentFrameData(progress, totalImages);

    let fromImage, toImage;

    if (frames[currIndex] === "place") {
      fromImage = placeCanvas;
    } else {
      fromImage = getSpriteCanvas(frames[currIndex], comicCanvas, imgProgress);
    }

    if (frames[nextIndex] === "place") {
      toImage = placeCanvas;
    } else {
      toImage = getSpriteCanvas(frames[nextIndex], comicCanvas);
    }

    // if in transition zone, set transition
    hippo.setSource({ media: fromImage, width: WIDTH, height: HEIGHT });
    dissolve.to = toImage;

    dissolve.progress = transitionProgress;

    hippo.draw();

    // eslint-disable-next-line
  }, [progress, hippo, dissolve, frames]);

  return (
    <div className={`${styles.bg}`}>
      <div className={styles.container}>
        <div className={styles.canvasHolder}>
          <div className={styles.overlay} />
          <canvas
            className={styles.canvas}
            // style={canvasStyle}
            ref={canvasRef}
            width={WIDTH}
            height={HEIGHT}
          />
        </div>
      </div>
    </div>
  );
}

function getCurrentFrameData(progress, totalImages) {
  // show each image for the same duration
  // the +1 is because the first img is only shown after the first duration
  // and the last img should be shown for a full duration after becoming visible
  const imgDuration = 1 / totalImages;
  let currIndex = Math.floor(progress / imgDuration);

  const transitionDuration = currIndex === 0 ? imgDuration : imgDuration / 2;

  let nextIndex = currIndex + 1;
  const lastIndex = totalImages;

  if (currIndex >= lastIndex - 1) {
    currIndex = lastIndex - 1;
    nextIndex = currIndex;
  }

  // do a transition when curr frame duration is halfTransDuration from end
  const transitionEnd = nextIndex * imgDuration;
  const transitionStart = transitionEnd - transitionDuration;

  const isTransitioning =
    progress >= transitionStart && progress <= transitionEnd;

  let transitionProgress = 0;

  if (isTransitioning) {
    const transitionTimeRemaining = transitionEnd - progress;
    const fractionFromTransitionEnd =
      transitionTimeRemaining / transitionDuration;

    transitionProgress = 1 - fractionFromTransitionEnd;
  }

  // make sure img fully greyscale before transition starts
  const imgStart = currIndex * imgDuration;
  const imgProgress =
    (progress - imgStart) / (imgDuration - transitionDuration);

  return {
    currIndex,
    nextIndex,
    isTransitioning,
    transitionProgress,
    imgProgress,
  };
}

function getSpriteCanvas(index, spritesheet, imgProgress = 0) {
  const spriteW = WIDTH;
  const spriteH = HEIGHT;
  const totalCols = 2;

  const col = index % totalCols;
  const row = Math.floor(index / totalCols);
  const x = spriteW * col;
  const y = spriteH * row;

  const canvas = document.createElement("canvas");
  canvas.width = spriteW;
  canvas.height = spriteH;
  const ctx = canvas.getContext("2d");
  ctx.filter = `grayscale(${imgProgress})`;
  ctx.drawImage(spritesheet, x, y, spriteW, spriteH, 0, 0, spriteW, spriteH);

  return canvas;
}

// function drawLocalSprite(indexes, ctx, spritesheet, w, h, progress) {
//   const spriteW = 640;
//   const spriteH = 480;

//   const totalCols = 2;

//   const totalImages = indexes.length;
//   const durationPerImg = 1 / totalImages;
//   let opacities = [];

//   for (let i = 0; i < totalImages; i++) {
//     const start = i * durationPerImg;
//     const end = start + durationPerImg;

//     opacities[i] = mapNum(progress, start, end, 0, 1);
//   }

//   const targW = w ? w : spriteW;
//   const targH = h ? h : spriteH;

//   ctx.clearRect(0, 0, targW, targH);

//   for (let spriteIndex = 0; spriteIndex < totalImages; spriteIndex++) {
//     const opacity = opacities[spriteIndex];

//     if (opacity > 0) {
//       const index = indexes[spriteIndex];

//       const col = index % totalCols;
//       const row = Math.floor(index / totalCols);

//       const x = spriteW * col;
//       const y = spriteH * row;

//       ctx.globalAlpha = opacities[spriteIndex];
//       ctx.drawImage(spritesheet, x, y, spriteW, spriteH, 0, 0, targW, targH);
//     }
//   }

//   // ctx.drawImage(spritesheet, x, y, spriteW, spriteH, 100, 0, targW, targH);
// }
